import React, { useEffect, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Box, Button, Flex, Image, useDisclosure, Badge } from '@chakra-ui/react';
import { Bell, BookOpenText, SignOut, UserCircle } from '@phosphor-icons/react';

import { Tooltip } from '@/components/ui/tooltip';
import {
  DialogActionTrigger,
  DialogBody,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogRoot
} from '@/components/ui/dialog';
import { apiGetUnreadCount } from '@/api/services/inbox.service';
import { fetchAPI } from '@/api/fetch';

interface IconProps {
  size?: number;
  color?: string;
  weight?: 'regular' | 'fill' | 'duotone';
}

export interface NavItem {
  id: number;
  name: string;
  icon: React.ReactElement<IconProps>;
  to: string;
  isActive: boolean;
}

interface SidebarProps {
  navItems: Omit<NavItem, 'isActive'>[];
  logo: string;
  logoAlt: string;
  bgColor?: string;
  onLogout: () => void;
  showInbox?: boolean;
  showProfile?: boolean;
  showTutorial?: boolean;
}

interface LogoutDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onLogout: () => void;
  cancelRef: React.RefObject<HTMLButtonElement | null>;
}

const LogoutDialog: React.FC<LogoutDialogProps> = ({ isOpen, onClose, onLogout, cancelRef }) => (
  <DialogRoot open={isOpen} onOpenChange={onClose}>
    <DialogContent>
      <DialogHeader fontSize="lg" fontWeight="bold">
        Logout
      </DialogHeader>

      <DialogBody fontSize="14px" fontWeight="400" lineHeight="1.33">
        Are you sure you want to logout? You will need to login again to access your account.
      </DialogBody>

      <DialogFooter>
        <DialogActionTrigger asChild>
          <Button
            w="120px"
            borderColor="primary.900"
            background="background.secondary"
            color="primary.900"
            borderRadius="8px"
            fontSize="16px"
            aria-label="Cancel"
            ref={cancelRef}
            _hover={{ bg: 'background.primary' }}
          >
            Cancel
          </Button>
        </DialogActionTrigger>
        <Button
          w="120px"
          background="primary.900"
          color="neutrals.0"
          borderRadius="8px"
          fontSize="16px"
          onClick={onLogout}
          ml={3}
          _hover={{ bg: 'primary.800' }}
        >
          Logout
        </Button>
      </DialogFooter>
    </DialogContent>
  </DialogRoot>
);

const SIDEBAR_WIDTH = '86px';
const ICON_SIZE = 24;
const PRIMARY_COLOR = '#4E76FF';
const WHITE_COLOR = '#FFFFFF';

export default function Sidebar({
  navItems,
  logo,
  logoAlt,
  bgColor = 'background.secondary',
  onLogout,
  showInbox = false,
  showProfile = false,
  showTutorial = false
}: SidebarProps) {
  const navigate = useNavigate();
  const location = useLocation();
  const { open, onOpen, onClose } = useDisclosure();
  const cancelRef = React.useRef<HTMLButtonElement>(null);
  const [unreadCount, setUnreadCount] = useState(0);

  // Fetch unread count on mount and poll every 30 seconds
  useEffect(() => {
    const fetchUnreadCount = async () => {
      if (showInbox) {
        try {
          const response = await fetchAPI({
            request: () => apiGetUnreadCount()
          });
          if (response.success && response.data) {
            setUnreadCount(response.data.unread_count);
          }
        } catch (error) {
          // Silent fail - don't disrupt user experience
          console.error('Failed to fetch unread count:', error);
        }
      }
    };

    fetchUnreadCount();
    const interval = setInterval(fetchUnreadCount, 30000); // Poll every 30 seconds

    // Listen for custom event from InboxIndex to update count immediately
    const handleUnreadCountChange = (event: CustomEvent) => {
      setUnreadCount(event.detail.unreadCount);
    };

    window.addEventListener('inbox-unread-count-changed', handleUnreadCountChange as EventListener);

    return () => {
      clearInterval(interval);
      window.removeEventListener('inbox-unread-count-changed', handleUnreadCountChange as EventListener);
    };
  }, [showInbox]);

  const updatedNavItems = navItems.map((item) => ({
    ...item,
    isActive: item.to === '/' ? location.pathname === '/' : location.pathname.startsWith(item.to)
  }));

  const handleLogout = () => {
    onLogout();
    onClose();
  };

  const renderNavItem = (nav: NavItem) => {
    const navContent = (
      <Box
        mt="20px"
        cursor="pointer"
        display="flex"
        flexDirection="column"
        alignItems="center"
        _hover={{
          '& > div:first-of-type': {
            bg: nav.isActive ? 'primary.900' : 'primary.50',
            transform: 'scale(1.05)'
          }
        }}
        transition="all 0.2s ease"
      >
        <Box
          h="32px"
          p="0 16px"
          display="flex"
          justifyContent="center"
          transition="all 0.2s ease"
          alignItems="center"
          bg={nav.isActive ? 'primary.900' : ''}
          borderRadius="100px"
        >
          {React.cloneElement(nav.icon, {
            color: nav.isActive ? WHITE_COLOR : PRIMARY_COLOR,
            weight: 'regular',
            size: ICON_SIZE
          })}
        </Box>
        <Box
          as="span"
          mt="4px"
          fontWeight="600"
          lineHeight="1.33"
          fontSize="12px"
          color="primary.900"
          textAlign="center"
          w="100%"
        >
          {nav.name}
        </Box>
      </Box>
    );

    return (
      <Link key={nav.id} to={nav.to} aria-current={nav.isActive ? 'page' : undefined}>
        {navContent}
      </Link>
    );
  };

  return (
    <>
      <Flex
        w={SIDEBAR_WIDTH}
        minW={SIDEBAR_WIDTH}
        p="40px 10px"
        flexDir="column"
        bg={bgColor}
        position="sticky"
        top="0"
        h="100vh"
        overflow="hidden"
        role="navigation"
        aria-label="Main navigation"
      >
        <Flex justifyContent="center" alignItems="center" flexDirection="column">
          <Image
            width="auto"
            height="40px"
            src={logo}
            alt={logoAlt}
            cursor="pointer"
            onClick={() => navigate('/')}
          />
          <Box height="20px" />
        </Flex>

        {updatedNavItems.map(renderNavItem)}

        <Flex flexDir="column" mt="auto">
          {showTutorial && (
            <Tooltip
              content="Access your guide here, anytime"
              openDelay={0}
              closeDelay={0}
              positioning={{ placement: 'right' }}
              contentProps={{
                css: { '--tooltip-bg': '#F8F9FB', color: 'black' }
              }}
              portalled
            >
              <Box
                mt="20px"
                cursor="pointer"
                display="flex"
                flexDirection="column"
                alignItems="center"
                _hover={{
                  '& > div:first-of-type': {
                    bg: 'primary.50',
                    transform: 'scale(1.05)'
                  }
                }}
                transition="all 0.2s ease"
                onClick={() => window.open('https://www.polarisjobs.com/documentation', '_blank')}
              >
                <Box
                  h="32px"
                  p="0 16px"
                  display="flex"
                  justifyContent="center"
                  transition="all 0.2s ease"
                  alignItems="center"
                  borderRadius="100px"
                >
                  <BookOpenText size={ICON_SIZE} color={PRIMARY_COLOR} weight="regular" />
                </Box>
                <Box
                  as="span"
                  mt="4px"
                  fontWeight="600"
                  lineHeight="1.33"
                  fontSize="12px"
                  color="primary.900"
                  textAlign="center"
                  w="100%"
                >
                  Tutorial
                </Box>
              </Box>
            </Tooltip>
          )}
          {showInbox && (
            <Link
              to="/inbox"
              aria-current={location.pathname.startsWith('/inbox') ? 'page' : undefined}
            >
              <Box
                mt="20px"
                cursor="pointer"
                display="flex"
                flexDirection="column"
                alignItems="center"
                _hover={{
                  '& > div:first-of-type': {
                    bg: location.pathname.startsWith('/inbox') ? 'primary.900' : 'primary.50',
                    transform: 'scale(1.05)'
                  }
                }}
                transition="all 0.2s ease"
                position="relative"
              >
                <Box
                  h="32px"
                  p="0 16px"
                  display="flex"
                  justifyContent="center"
                  transition="all 0.2s ease"
                  alignItems="center"
                  bg={location.pathname.startsWith('/inbox') ? 'primary.900' : ''}
                  borderRadius="100px"
                  position="relative"
                >
                  <Bell
                    size={ICON_SIZE}
                    color={location.pathname.startsWith('/inbox') ? WHITE_COLOR : PRIMARY_COLOR}
                  />
                  {unreadCount > 0 && (
                    <Badge
                      position="absolute"
                      top="-4px"
                      right="-4px"
                      bg="red.500"
                      color="white"
                      borderRadius="full"
                      minW="18px"
                      h="20px"
                      w="20px"
                      fontSize="10px"
                      fontWeight="bold"
                      display="flex"
                      alignItems="center"
                      justifyContent="center"
                      px="4px"
                    >
                      {unreadCount > 99 ? '99+' : unreadCount}
                    </Badge>
                  )}
                </Box>
                <Box
                  as="span"
                  mt="4px"
                  fontWeight="600"
                  lineHeight="1.33"
                  fontSize="12px"
                  color="primary.900"
                  textAlign="center"
                  w="100%"
                >
                  Notification
                </Box>
              </Box>
            </Link>
          )}
          {showProfile && (
            <Link
              to="/profile"
              aria-current={location.pathname.startsWith('/profile') ? 'page' : undefined}
            >
              <Box
                mt="20px"
                cursor="pointer"
                display="flex"
                flexDirection="column"
                alignItems="center"
                _hover={{
                  '& > div:first-of-type': {
                    bg: location.pathname.startsWith('/profile') ? 'primary.900' : 'primary.50',
                    transform: 'scale(1.05)'
                  }
                }}
                transition="all 0.2s ease"
              >
                <Box
                  h="32px"
                  p="0 16px"
                  display="flex"
                  justifyContent="center"
                  transition="all 0.2s ease"
                  alignItems="center"
                  bg={location.pathname.startsWith('/profile') ? 'primary.900' : ''}
                  borderRadius="100px"
                >
                  <UserCircle
                    size={ICON_SIZE}
                    color={location.pathname.startsWith('/profile') ? WHITE_COLOR : PRIMARY_COLOR}
                  />
                </Box>
                <Box
                  as="span"
                  mt="4px"
                  fontWeight="600"
                  lineHeight="1.33"
                  fontSize="12px"
                  color="primary.900"
                  textAlign="center"
                  w="100%"
                >
                  Profile
                </Box>
              </Box>
            </Link>
          )}
          <Box
            mt="20px"
            cursor="pointer"
            display="flex"
            flexDirection="column"
            alignItems="center"
            _hover={{
              '& > div:first-of-type': {
                bg: 'primary.50',
                transform: 'scale(1.05)'
              }
            }}
            transition="all 0.2s ease"
            onClick={onOpen}
          >
            <Box
              h="32px"
              p="0 16px"
              display="flex"
              justifyContent="center"
              transition="all 0.2s ease"
              alignItems="center"
              borderRadius="100px"
            >
              <SignOut size={ICON_SIZE} color={PRIMARY_COLOR} />
            </Box>
            <Box
              as="span"
              mt="4px"
              fontWeight="600"
              lineHeight="1.33"
              fontSize="12px"
              color="primary.900"
              textAlign="center"
              w="100%"
            >
              Logout
            </Box>
          </Box>
        </Flex>
      </Flex>

      <LogoutDialog isOpen={open} onClose={onClose} onLogout={handleLogout} cancelRef={cancelRef} />
    </>
  );
}
